import { useEffect, useMemo, useState } from "react";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, ExternalLink, ArrowLeft, X } from "lucide-react";
import { Swatch, domain } from "./helpers";

const norm = (s) => (s || "").trim().toLowerCase();

function DecorRow({ d, selected, onPick }) {
  return (
    <CommandItem value={`${d.name} ${d.supplier || ""}`} onSelect={() => onPick(d.id)}
      data-testid={`decor-opt-${d.id}`} className={`gap-2.5 ${selected ? "bg-success/10" : ""}`}>
      <Swatch decor={d} size={26} />
      <div className="min-w-0 flex-1">
        <div className="font-semibold truncate text-[14px]">{d.name}</div>
        <div className="text-[12px] text-muted-foreground truncate">{d.supplier} · {d.category}</div>
      </div>
      {selected && <Check size={16} className="text-success shrink-0" />}
    </CommandItem>
  );
}

// Mini-formulář vlastního odkazu (§F2, zjednodušeno v zadání 79/B — jen odkaz;
// název se převezme z labelu položky až v handleru).
function CustomForm({ onBack, onSubmit }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = /^https?:\/\//i.test(url.trim());
  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try { await onSubmit({ url: url.trim() }); } finally { setBusy(false); }
  };
  return (
    <div className="p-3 space-y-3" data-testid="decor-picker-custom-form">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Zpět na vzorník</button>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Odkaz na produkt <span className="text-brand">*</span></label>
        <Input autoFocus value={url} onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="https://…" className="h-11 mt-1" data-testid="custom-url" />
      </div>
      <Button onClick={submit} disabled={!valid || busy} className="w-full h-11 font-bold" data-testid="custom-submit">Použít odkaz</Button>
    </div>
  );
}

// Sdílený seznam dekorů (§E tvrdý filtr + §F vlastní odkaz). Kategorie položky
// natvrdo přefiltruje nabídku; hledá se jen uvnitř kategorie.
export function DecorPickerList({ decors, value, onPick, preferredCategory, onPickCustom, customCurrent, onClearCustom, mixedMessage }) {
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("list"); // list | custom
  const hasPref = !!(preferredCategory && String(preferredCategory).trim());
  const prefKey = hasPref ? norm(preferredCategory) : null;

  useEffect(() => { setQuery(""); setMode("list"); }, [prefKey, customCurrent]);

  const selected = useMemo(() => decors.find((d) => d.id === value), [decors, value]);
  const list = useMemo(() => {
    if (!hasPref) return decors;
    const inCat = decors.filter((d) => norm(d.category) === prefKey);
    // Vybraný dekor z jiné kategorie připnout navrch, ať je fajfka vždy vidět.
    if (selected && norm(selected.category) !== prefKey) return [selected, ...inCat];
    return inCat;
  }, [decors, hasPref, prefKey, selected]);
  const prefCount = hasPref ? decors.filter((d) => norm(d.category) === prefKey).length : decors.length;

  if (mixedMessage) {
    return <div className="p-4 text-[13px] text-muted-foreground text-center" data-testid="bulk-decor-mixed">{mixedMessage}</div>;
  }
  if (mode === "custom") {
    return <CustomForm onBack={() => setMode("list")} onSubmit={onPickCustom} />;
  }

  return (
    <div className="flex flex-col">
      {customCurrent && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/50" data-testid="decor-picker-custom-current">
          <ExternalLink size={15} className="text-muted-foreground shrink-0" />
          <span className="text-[13px] font-semibold truncate flex-1">Vlastní odkaz: {customCurrent}</span>
          {onClearCustom && <button type="button" onClick={onClearCustom} className="text-muted-foreground hover:text-destructive shrink-0" data-testid="decor-picker-custom-clear"><X size={15} /></button>}
        </div>
      )}
      <Command shouldFilter>
        <CommandInput placeholder={hasPref ? `Hledat v „${preferredCategory}"` : "Hledat ve vzorníku"} value={query} onValueChange={setQuery} data-testid="decor-picker-search" />
        <CommandList className="max-h-[300px]">
          <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">
            {hasPref ? <>Nic v kategorii {preferredCategory}. Jiný dekor? Změňte kategorii položky.</> : "Nic ve vzorníku."}
          </CommandEmpty>
          {!hasPref && decors.length > 20 && (
            <div className="text-[12px] text-muted-foreground px-3 py-1.5" data-testid="decor-picker-hint">Vyplňte položce kategorii a nabídka se zúží jen na ni.</div>
          )}
          {hasPref && prefCount === 0 ? (
            <div className="px-3 py-3 text-[13px] text-muted-foreground" data-testid="decor-picker-empty-cat">
              V kategorii {preferredCategory} zatím není žádný dekor
            </div>
          ) : (
            <CommandGroup heading={hasPref ? `${preferredCategory} · ${prefCount}` : undefined}>
              {list.map((d) => <DecorRow key={d.id} d={d} selected={d.id === value} onPick={onPick} />)}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
      {onPickCustom && (
        <button type="button" onClick={() => setMode("custom")} data-testid="decor-picker-custom"
          className="flex items-center gap-2 px-3 py-2.5 border-t border-border text-[13px] font-semibold text-brand hover:bg-muted/50">
          <ExternalLink size={15} /> Vložit vlastní odkaz…
        </button>
      )}
    </div>
  );
}
